import { directSupabase as supabase } from '../../lib/supabaseClient';
import { documentsService } from '../docs/services/documents.service';
import type { Document, DocumentWithLines } from '../docs/types/documents.types';
import { accountsService } from './accounts.service';
import {
  arrangementDeleteBlock,
  fromDbArrangement,
  planArrangementInvoice,
  toDbArrangement,
  validateArrangementEdit,
  validateArrangementInput,
  vatTreatmentNote,
  cisNote,
  type ArrangementInvoiceKind,
  type BillingArrangement,
  type BillingArrangementInput,
} from './arrangements.model';

/** Non-customer invoices are raised under Keystone South UK. */
export const ACCOUNT_INVOICING_ORG_CODE = 'KSU';

type Organisation = { id: string; name: string; code: string };

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; code?: string };
  if (row.code === '42P01') return 'Account invoicing is not set up in the database yet. Apply the KSU account invoicing migration.';
  return row.message || fallback;
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

let organisationCache: Organisation | null = null;

export const arrangementsService = {
  async organisation(): Promise<Organisation> {
    if (organisationCache) return organisationCache;
    const { data, error } = await supabase
      .from('organisations')
      .select('id, name, code')
      .eq('code', ACCOUNT_INVOICING_ORG_CODE)
      .maybeSingle();
    if (error) throw new Error(errorMessage(error, 'Failed to load the KSU organisation'));
    if (!data) {
      throw new Error('The Keystone South UK (KSU) organisation does not exist yet. Apply the KSU account invoicing migration.');
    }
    organisationCache = data as Organisation;
    return organisationCache;
  },

  async list(): Promise<BillingArrangement[]> {
    const { data, error } = await supabase
      .from('billing_arrangements')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(errorMessage(error, 'Failed to load arrangements'));
    return (data || []).map(fromDbArrangement);
  },

  async get(id: string): Promise<BillingArrangement | null> {
    const { data, error } = await supabase
      .from('billing_arrangements')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(errorMessage(error, 'Failed to load the arrangement'));
    return data ? fromDbArrangement(data) : null;
  },

  /** Looks an arrangement up by its KSU reference, e.g. KSU-0001. */
  async getByRef(lifecycleRef: string): Promise<BillingArrangement | null> {
    const org = await this.organisation();
    const { data, error } = await supabase
      .from('billing_arrangements')
      .select('*')
      .eq('organisation_id', org.id)
      .eq('lifecycle_ref', lifecycleRef.trim().toUpperCase())
      .maybeSingle();
    if (error) throw new Error(errorMessage(error, 'Failed to load the arrangement'));
    return data ? fromDbArrangement(data) : null;
  },

  /** Mints the arrangement's KSU reference, then saves it. */
  async create(input: BillingArrangementInput): Promise<BillingArrangement> {
    const problem = validateArrangementInput(input);
    if (problem) throw new Error(problem);
    const org = await this.organisation();
    const lifecycleRef = await documentsService.mintLifecycleRef(org.id);
    const { data, error } = await supabase
      .from('billing_arrangements')
      .insert({
        ...toDbArrangement(input),
        organisation_id: org.id,
        lifecycle_ref: lifecycleRef,
        created_by: await currentUserId(),
      })
      .select('*')
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to create the arrangement'));
    return fromDbArrangement(data);
  },

  /** Edits an arrangement. The KSU reference never changes. See validateArrangementEdit for what is locked. */
  async update(id: string, input: BillingArrangementInput): Promise<BillingArrangement> {
    const arrangement = await this.get(id);
    if (!arrangement) throw new Error('Arrangement not found');
    const invoices = await this.invoices(arrangement);
    const problem = validateArrangementEdit(arrangement, input, invoices.map((invoice) => ({
      id: invoice.id,
      role: invoice.invoiceRole,
      status: invoice.status,
      grossTotal: invoice.grossTotal,
    })));
    if (problem) throw new Error(problem);
    const { data, error } = await supabase
      .from('billing_arrangements')
      .update(toDbArrangement(input))
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to save the arrangement'));
    return fromDbArrangement(data);
  },

  /**
   * Deletes an arrangement and its draft invoices. Refused once any invoice has been
   * issued or sent to FreeAgent (see arrangementDeleteBlock). Its KSU number is not reused.
   */
  async remove(id: string): Promise<void> {
    const arrangement = await this.get(id);
    if (!arrangement) throw new Error('Arrangement not found');
    const grouped = await this.invoicesByArrangement([arrangement]);
    const invoices = grouped[arrangement.id] || [];
    const blocked = arrangementDeleteBlock(invoices);
    if (blocked) throw new Error(`${arrangement.lifecycleRef} cannot be deleted. ${blocked}`);
    for (const invoice of invoices) {
      await documentsService.deleteDocument(String(invoice.id));
    }
    const { error } = await supabase.from('billing_arrangements').delete().eq('id', id);
    if (error) throw new Error(errorMessage(error, 'Failed to delete the arrangement'));
  },

  async setStatus(id: string, status: 'open' | 'closed'): Promise<BillingArrangement> {
    const { data, error } = await supabase
      .from('billing_arrangements')
      .update({ status })
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to update the arrangement'));
    return fromDbArrangement(data);
  },

  async invoices(arrangement: BillingArrangement): Promise<Document[]> {
    return documentsService.listLifecycleInvoices(arrangement.organisationId, arrangement.lifecycleRef);
  },

  /** Invoices for many arrangements in one query, keyed by arrangement id. */
  async invoicesByArrangement(arrangements: BillingArrangement[]): Promise<Record<string, Array<Record<string, any>>>> {
    const grouped: Record<string, Array<Record<string, any>>> = {};
    if (arrangements.length === 0) return grouped;
    const refs = arrangements.map((row) => row.lifecycleRef);
    const { data, error } = await supabase
      .from('documents')
      .select('id, organisation_id, lifecycle_ref, invoice_ref, invoice_role, source_survey_event, status, gross_total, issued_at, created_at, freeagent_invoice_url')
      .eq('kind', 'Invoice')
      .in('lifecycle_ref', refs);
    if (error) throw new Error(errorMessage(error, 'Failed to load invoices'));
    for (const arrangement of arrangements) {
      grouped[arrangement.id] = (data || []).filter((row: any) => (
        row.lifecycle_ref === arrangement.lifecycleRef && row.organisation_id === arrangement.organisationId
      ));
    }
    return grouped;
  },

  /**
   * One step for a single invoice: creates an open-ended arrangement, raises its one invoice,
   * then closes the arrangement. It can be reopened from the Invoicing page if more are needed.
   */
  async createOneOffInvoice(input: {
    accountId: string;
    arrangementType?: BillingArrangementInput['arrangementType'];
    title: string;
    description?: string | null;
    amountNet: number;
    lineName?: string | null;
    vatTreatment: BillingArrangementInput['vatTreatment'];
    vatPct?: number;
    paymentTermsInDays?: number;
    cisBand?: BillingArrangementInput['cisBand'];
    cisLabourNet?: number | null;
  }): Promise<{ arrangement: BillingArrangement; invoice: DocumentWithLines }> {
    if (!(Number(input.amountNet) > 0)) throw new Error('Enter an amount greater than zero.');
    const arrangement = await this.create({
      accountId: input.accountId,
      arrangementType: input.arrangementType,
      title: input.title,
      description: input.description,
      agreedNet: null,
      vatTreatment: input.vatTreatment,
      vatPct: input.vatPct,
      depositPercentage: null,
      paymentTermsInDays: input.paymentTermsInDays,
      cisBand: input.cisBand,
    });
    let invoice: DocumentWithLines;
    try {
      invoice = await this.issueInvoice({
        arrangementId: arrangement.id,
        kind: 'one_off',
        amountNet: input.amountNet,
        lineName: input.lineName,
        cisLabourNet: input.cisLabourNet,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create the invoice';
      throw new Error(`${message} Arrangement ${arrangement.lifecycleRef} was created without an invoice; open it to try again.`);
    }
    const closed = await this.setStatus(arrangement.id, 'closed').catch(() => arrangement);
    return { arrangement: closed, invoice };
  },

  /** Plans and creates the next invoice on an arrangement. Returns the new invoice. */
  async issueInvoice(args: {
    arrangementId: string;
    kind: ArrangementInvoiceKind;
    amountNet?: number | null;
    lineName?: string | null;
    /** Labour in this invoice, ex VAT, when CIS applies. Defaults to the whole net. */
    cisLabourNet?: number | null;
  }): Promise<DocumentWithLines> {
    const arrangement = await this.get(args.arrangementId);
    if (!arrangement) throw new Error('Arrangement not found');
    const account = await accountsService.get(arrangement.accountId);
    if (!account) throw new Error('The account on this arrangement no longer exists.');
    const existing = await this.invoices(arrangement);
    const plan = planArrangementInvoice({
      arrangement,
      invoices: existing.map((invoice) => ({
        id: invoice.id,
        role: invoice.invoiceRole,
        status: invoice.status,
        grossTotal: invoice.grossTotal,
        depositAmount: invoice.depositAmount,
      })),
      kind: args.kind,
      amountNet: args.amountNet,
      lineName: args.lineName,
      cisLabourNet: args.cisLabourNet,
    });

    const note = vatTreatmentNote({
      treatment: arrangement.vatTreatment,
      amountNet: plan.amountNet,
      vatPct: arrangement.vatPct,
    });
    const notes = [arrangement.description, note, plan.cis ? cisNote(plan.cis) : null].filter(Boolean).join('\n\n') || null;
    if (plan.period) {
      const alreadyBilled = existing.some((invoice) => (
        String(invoice.status || '').toLowerCase() !== 'void' && invoice.sourceSurveyEvent === plan.sourceEvent
      ));
      if (alreadyBilled) throw new Error('That period has already been invoiced. Reload the page.');
    }
    const invoice = await documentsService.createDocument({
      kind: 'Invoice',
      lifecycleRef: arrangement.lifecycleRef,
      organisationId: arrangement.organisationId,
      isOriginal: false,
      projectId: null,
      customerId: null,
      accountId: account.id,
      vatTreatment: arrangement.vatTreatment,
      title: plan.title,
      description: arrangement.description,
      notes,
      status: 'draft',
      currency: 'GBP',
      depositAmount: plan.kind === 'deposit' ? plan.amountGross : 0,
      depositPercentage: plan.kind === 'deposit' ? arrangement.depositPercentage : null,
      hasDeposit: plan.kind === 'deposit',
      vatEnabled: plan.vatEnabled,
      vatPct: arrangement.vatPct,
      inputVatSelected: false,
      issuedAt: null,
      paymentTermsInDays: plan.kind === 'deposit' ? 0 : arrangement.paymentTermsInDays,
      paymentStructureApplicable: false,
      paymentStructurePattern: null,
      invoiceRole: plan.role,
      frozenAt: plan.frozen ? new Date().toISOString() : null,
      sourceSurveyEvent: plan.sourceEvent,
      fees: { adminPct: 0, adminCap: 0, contingencyPct: 0, profitPct: 0, developerPct: null },
      source: 'jarvis',
    }, [{
      lineKind: 'other',
      additionalType: 'other',
      name: plan.lineName,
      description: null,
      qty: 1,
      unit: 'item',
      unitPrice: plan.amountGross,
      vatInclusive: plan.vatEnabled,
      sortOrder: 0,
    }]);

    if (plan.cis) {
      const { error: cisError } = await supabase
        .from('documents')
        .update({ cis_band: plan.cis.band, cis_labour_net: plan.cis.labourNet, cis_deduction: plan.cis.deduction })
        .eq('id', invoice.id);
      if (cisError) {
        throw new Error(`Invoice ${invoice.invoiceRef || ''} was created, but its CIS details could not be saved: ${cisError.message}`);
      }
    }

    if (plan.period) {
      // Move on to the next period. Matching the old index stops two clicks advancing twice.
      const { error } = await supabase
        .from('billing_arrangements')
        .update({ next_period_index: plan.period.index + 1 })
        .eq('id', arrangement.id)
        .eq('next_period_index', plan.period.index);
      if (error) {
        throw new Error(`Invoice ${invoice.invoiceRef || ''} was created, but the arrangement could not move to the next period: ${error.message}`);
      }
    }
    return invoice;
  },
};
